import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import FilterBar from "../components/FilterBar";
import RecipeGrid from "../components/RecipeGrid";
import { useRecipes } from "../hooks/useRecipes";
import { useAuth } from "../context/AuthContext";
import type { ListRecipesParams } from "../types";

export default function HomePage() {
  const [filterParams, setFilterParams] = useState<ListRecipesParams>({});
  const [searchText, setSearchText] = useState("");
  const { user } = useAuth();

  const { data: recipes, isLoading, error } = useRecipes(filterParams);

  // Client-side title search applied on top of API results
  const displayed = useMemo(() => {
    if (!recipes) return [];
    if (!searchText.trim()) return recipes;
    const q = searchText.toLowerCase();
    return recipes.filter((r) => r.title.toLowerCase().includes(q));
  }, [recipes, searchText]);

  return (
    <>
      <Header onSearchChange={setSearchText} searchValue={searchText} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <FilterBar params={filterParams} onChange={setFilterParams} />
          {user && (
            <Link
              to="/recipe/new"
              className="flex-shrink-0 text-sm font-medium text-white bg-herb hover:bg-herb-light px-4 py-2 rounded-full transition-colors"
            >
              + Add Recipe
            </Link>
          )}
        </div>

        {isLoading && (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl bg-stone-100 animate-pulse"
                style={{ aspectRatio: "1" }}
              />
            ))}
          </div>
        )}

        {error && (
          <div className="text-center py-10 text-red-500">
            <p className="font-medium">Failed to load recipes.</p>
            <p className="text-sm mt-1 text-red-400">
              {error instanceof Error ? error.message : "Please try again."}
            </p>
          </div>
        )}

        {!isLoading && !error && <RecipeGrid recipes={displayed} />}
      </div>
    </>
  );
}
